import { z } from 'zod';
import { membershipStatusSchema } from './role';

// A slug is a public URL segment: the venue's booking page is /{slug}. See §3.
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2)
  .max(40)
  .regex(SLUG_PATTERN, 'must be lowercase letters, digits and single hyphens');

// A form sends a cleared field as an empty string; that means "unset", not "".
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .default(null)
    .transform((value) => (value === null || value === '' ? null : value));

// A form sends a cleared number as an empty string, which means "unset" too.
const optionalNumber = (min: number, max: number) =>
  z
    .union([z.number(), z.literal(''), z.null()])
    .default(null)
    .transform((value) => (value === '' || value === null ? null : value))
    .refine((value) => value === null || (value >= min && value <= max), 'is off the globe');

// A formatted address is not a line somebody types: OSM's own runs past 150
// characters in Greek, and the field is filled from it rather than by hand. §3
export const MAX_LOCATION = 300;

// Type and spot kind are free text on purpose: a new venue kind is data entry.
export const venueProfileSchema = z.object({
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  venueType: z.string().trim().min(1).max(60),
  spotKind: z.string().trim().min(1).max(40),
  // The address a map search filled in, and the place it stands for. §3, Phase 8
  location: optionalText(MAX_LOCATION),
  placeId: optionalText(300),
  latitude: optionalNumber(-90, 90),
  longitude: optionalNumber(-180, 180),
  ownerDisplayName: optionalText(120),
});

export type VenueProfile = z.infer<typeof venueProfileSchema>;

export const tenantOwnerSchema = z.object({
  // The membership, so the operator can take an owner off the venue again.
  id: z.uuid(),
  name: z.string().nullable(),
  email: z.string(),
  status: membershipStatusSchema,
});

export type TenantOwner = z.infer<typeof tenantOwnerSchema>;

// One row per venue on the platform; freeToday is today's live free count. §17
export const tenantSchema = z.object({
  venueId: z.uuid(),
  name: z.string(),
  slug: z.string(),
  venueType: z.string(),
  spotKind: z.string(),
  location: z.string().nullable(),
  ownerDisplayName: z.string().nullable(),
  // Where the venue's uploaded logo is served, versioned so a cache lets go. §3
  logoUrl: z.string().nullable(),
  placeId: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  spotCount: z.number().int().nonnegative(),
  freeToday: z.number().int().nonnegative(),
  // A venue may be owned by several people (§3); the card names them all.
  owners: z.array(tenantOwnerSchema),
});

export type Tenant = z.infer<typeof tenantSchema>;

export const tenantsResponseSchema = z.object({ tenants: z.array(tenantSchema) });

export type TenantsResponse = z.infer<typeof tenantsResponseSchema>;

// The one role the team screen refuses to grant, so the operator grants it here.
export const ownerRegistrationSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.email(),
});

export type OwnerRegistration = z.infer<typeof ownerRegistrationSchema>;

// §4: a name belongs to the account, and an owner's is correctable nowhere else.
export const ownerRenameSchema = ownerRegistrationSchema.pick({ name: true });

export type OwnerRename = z.infer<typeof ownerRenameSchema>;

// A logo is small enough for a row and for a request; bigger is a mistake. §3
export const LOGO_MAX_BYTES = 256 * 1024;

// §3: three characters is the least worth asking OSM about.
export const PLACE_QUERY_MIN = 3;

export const placeQuerySchema = z.object({
  q: z.string().trim().min(PLACE_QUERY_MIN).max(200),
});

// A hit carries its own name, its address and where it is; picking asks no more.
export const placeSuggestionSchema = z.object({
  placeId: z.string().min(1),
  name: z.string(),
  description: z.string().min(1),
  latitude: z.number(),
  longitude: z.number(),
});

export type PlaceSuggestion = z.infer<typeof placeSuggestionSchema>;

export const placeSuggestionsSchema = z.object({ places: z.array(placeSuggestionSchema) });

export type PlaceSuggestions = z.infer<typeof placeSuggestionsSchema>;

// A pin dropped where OSM knows nothing still has coordinates and no place.
export const placeDetailsSchema = z.object({
  placeId: z.string().min(1).nullable(),
  address: z.string(),
  latitude: z.number(),
  longitude: z.number(),
});

// A pasted Google Maps link, which the server resolves into a place. §3
export const placeLinkQuerySchema = z.object({ url: z.string().trim().min(1).max(2000) });

export type PlaceDetails = z.infer<typeof placeDetailsSchema>;
